/* eslint-disable no-param-reassign */
import createBaseSurveyMeta from '../_BASE';
import colors from '../../../Colors';
import {
	computeUuid,
	computeFormattedStationId,
	mapGpsDistance,
	computeDistance,
} from '../../util/readings.parse.computeProps';
import { createComputeYMinYMax } from './util/computeYMinYMax';
import {
	createReadingKeyProps,
	createGetReadingKeys,
	createGetReadingKeyProps,
	createGetColumns,
	createGetSurveyColor,
	createTransformToCsvJson
} from '../util';

const INVERT_Y_AXIS = false;

const isDefined = v => v !== undefined && v !== null;

const SECOND_Y_AXIS_DEFINITION = {
	id: 'y2',
	position: 'right',

	// grid line settings
	grid: {
		drawOnChartArea: false, // only want the grid lines for one axis to show up
		drawBorder: false,
	},
	afterFit: scaleInstance => {
		scaleInstance.width = 55; // sets the width to 55px
	}
}

// eslint-disable-next-line no-unused-vars
export default _survey => {
	// CONSTANTS

	const gapThreshold = 200;

	// READING KEYS

	const readingKeys = [
		// @wb uncomment when ready to add double yaxis back in
		// {
		// 	label: 'dB{µV}-N1A',
		// 	value: 'normdecmicrov',
		// 	seriesType: 'bar',
		// 	isPercentage: true,
		// 	color: colors.getColor(),
		// 	yAxisID: 'y2',
		// 	yAxisUnitDisplayText: 'dB{µV}-N1A'
		// },
		{
			label: 'dB{µV}',
			value: 'decmicrov',
			seriesType: 'line',
			isPercentage: true,
			color: colors.getColor(),
			yAxisID: 'y',
			yAxisUnitDisplayText: 'dB{µV}'
		},
	];
	const readingKeyProps = createReadingKeyProps(readingKeys);
	const computeYMinYMax = createComputeYMinYMax(readingKeys);

	// SKIP READING

	const isSkipReading = reading =>
		reading &&
		readingKeys.every(
			rk => reading[rk.value] === 0 || reading[rk.value] === ''
		);

	// HAS GAP

	const hasGap = (currReading, prevReading) => {
		return !!prevReading && currReading.id - prevReading.id > gapThreshold;
	};

	// INTERPOLATE

	const interpolate = (
		// eslint-disable-next-line no-unused-vars
		_prevDepolReading,
		// eslint-disable-next-line no-unused-vars
		_nextDepolReading,
		// eslint-disable-next-line no-unused-vars
		_alignToReading
	) => {};

	const computeHilo = r => {
		r.low = r.low || 0;
	};

	// COMPUTE PROPS - to be used by readings.parse
	const computeProps = (survey, _prevReading, currReading, currIdx) => {
		computeUuid(currReading, currIdx);
		computeFormattedStationId(currReading);
		computeHilo(currReading);
		computeYMinYMax(survey, currReading);
		mapGpsDistance(currReading);
		computeDistance(_prevReading, currReading);
	};

	// SIMPLIFIED READINGS - creates a smaller data set to be used for rendering polyline on map
	// most survey types will not have simplified readings
	// eslint-disable-next-line no-unused-vars
	const createSimplifiedReadings = ({ readings, subtype }) => undefined;

	const columns = [
		{
			label: 'ID',
			value: 'id',
			checked: true,
			width: 48,
			required: true
		},
		{
			label: 'Station',
			value: 'formattedDst',
			checked: true,
			width: 80,
			showInTooltip: false
		},
		{ label: 'dB{µV}', value: 'decmicrov', checked: true, width: 54 },
		{ label: 'dB{µV}-N1A', value: 'normdecmicrov', checked: true, width: 54 },
		{ label: 'INITIAL CURRENT', value: 'initialcurrent', checked: true, width: 54 },
		{
			label: 'LAT',
			value: 'lat',
			checked: true,
			width: 65,
			getCsvValue: r => {
				if (isDefined(r.lat)) {
					return r.lat;
				}
				if (isDefined(r.loc)) {
					return r.loc[0];
				}
				return undefined;
			}
		},
		{
			label: 'LNG',
			value: 'lng',
			checked: true,
			width: 70,
			getCsvValue: r => {
				if (isDefined(r.lng)) {
					return r.lng;
				}
				if (isDefined(r.loc)) {
					return r.loc[1];
				}
				return undefined;
			}
		},
		{
			label: 'COMMENTS',
			value: 'cmt',
			checked: true,
			width: 255,
			type: 'text'
		}
	];
	// MAIN
	const getReadingKeys = createGetReadingKeys(readingKeys);
	const getColumns = createGetColumns(columns);
	const transformToCsvJson = createTransformToCsvJson(getColumns);

	const meta = {
		...createBaseSurveyMeta(_survey),
		isChartInversed: INVERT_Y_AXIS,
		getReadingKeys,
		getReadingKeyProps: createGetReadingKeyProps(readingKeyProps),
		getColumns,
		transformToCsvJson,
		getSurveyColor: createGetSurveyColor(getReadingKeys),
		// secondYAxisDefinition: SECOND_Y_AXIS_DEFINITION,  // @wb uncomment when ready to add double yaxis back in


		hasThreshold: true,
		lessNegativeThreshold: 75, // greater than on non inverted y-axis
		moreNegativeThreshold: 50, // less than on non inverted y-axis

		fkey: 'decmicrov',
		isSkipReading,
		hasGap,
		interpolate,
		computeProps,
		computeYMinYMax,
		createSimplifiedReadings
	};

	return meta;
};
